// The manifest kore's SearchBar renders for the screens table: which facets
// a user may filter on, with which operators, and which columns sort.
//
// `locationId` / `templateId` are `Enum` defs, not `Id`: kore's `IdPicker` has no
// options mechanism at all (free-typed raw ids, no label resolution — see
// the task-10 report for the full trail through `id-picker/index.tsx` and
// `search-bar/index.tsx`'s `chipValueOf`), so a real picker needs `Enum`
// instead, with `values` built from `useLocations`/`useTemplates` data at the
// call site (`screensSearchManifest`, below). kore's `EnumOp` and the
// server's `IdOp` are byte-identical on the wire for `Eq` —
// `{"type":"Eq","values":[...]}` — so this sends exactly what `Id` would
// have sent, while `chipValueOf` (which resolves labels only for
// `Enum`/`Dynamic`, never `Id`) now shows the location/template name in the
// committed chip, and `EnumPicker` grows a search box past ten values.
//
// Every operator listed is one `screens_search.rs` actually translates.
import { type FilterDef, FilterOperator, type FilterResponse } from "@pej/kore/components";
import type { BoolOp, IdOp, NumberOp, ScreenFilters, ScreenSearchQueryField, ScreenSortField } from "@/api/types";

/** Minimal shape `screenFilterDefs` needs from a location/template — enough for
 *  `LocationListEntry` and `Template` to both satisfy it without an import of
 *  either. */
interface NamedOption {
    id: string;
    name: string;
}

function toFilterValues(options: readonly NamedOption[]): { value: string; label: string }[] {
    return options.map((o) => ({ value: o.id, label: o.name }));
}

/**
 * A factory, not a constant: `locationId`/`templateId` need `values` built from
 * data the page loads via `useLocations`/`useTemplates`. `locations`/`templates`
 * default to `[]` so the manifest is renderable before either query
 * resolves — kore's `SearchBar` treats an empty `values` list as "nothing to
 * pick yet", not an error.
 *
 * Both option lists are only as complete as `GET /locations` and
 * `GET /templates` — currently unpaginated. If either endpoint grows
 * pagination, this picker will silently omit entries past the first page:
 * the filter will look broken ("my location isn't in the list") rather than
 * visibly incomplete, since there is no total count to compare against here.
 */
function screenFilterDefs(locations: readonly NamedOption[], templates: readonly NamedOption[]): FilterDef[] {
    return [
        {
            type: "Enum",
            filterParam: "locationId",
            label: "Location",
            // `Eq` only — see `idFilterFromChip` below for why the
            // translation layer still has to guard this rather than trust
            // the def.
            operators: [FilterOperator.Eq],
            values: toFilterValues(locations),
        },
        {
            // NOT nullable, though a screen may legitimately have no
            // template: the server's filter is a plain `IdOp` with no null
            // in its value set, so offering "no template" here would send
            // something the endpoint cannot represent. Filtering for
            // template-less screens needs kore's `NullableIdOp` mirrored in
            // kore first — additive on both sides, deferred.
            type: "Enum",
            filterParam: "templateId",
            label: "Template",
            operators: [FilterOperator.Eq],
            values: toFilterValues(templates),
        },
        {
            type: "Bool",
            filterParam: "active",
            label: "Active",
            operators: [FilterOperator.Eq],
        },
        {
            // Enum, not Number: the operational question is whether an
            // offset is in force, not what its value is. The chip maps to a
            // NumberOp below, where the request is built.
            type: "Enum",
            filterParam: "offsetMinutes",
            label: "Offset",
            operators: [FilterOperator.Eq],
            values: [
                { value: "applied", label: "Applied" },
                { value: "none", label: "None" },
            ],
        },
    ];
}

/**
 * `sortBy.fields` carries the server's `ScreenSortField` names verbatim —
 * "Name" | "LocationName" | "Offset" — because kore matches them against
 * DataTable column keys (see screens.index.tsx).
 *
 * `query.fields` mirrors `ScreenSearchQueryField` — "Name" | "LocationName" —
 * the two columns `screens_search.rs` actually searches. Declaring both
 * gives kore's SearchBar a scope chip ("All" / "Name" / "Location"); the page
 * validates whatever comes back before it reaches the request
 * (`isScreenQueryField` in screens.index.tsx) so a manifest/wire drift can't
 * ship a `searchBy` the server would reject.
 *
 * No `query.placeholder` here, unlike `locations-filters.ts`: once `fields` is
 * non-empty, `SearchBar`'s own placeholder derivation always builds "Search
 * by name, location…" from the field labels and never falls back to a manifest
 * placeholder — only the page's explicit `searchPlaceholder` (passed to
 * `useKoreSearch` in screens.index.tsx) can override it, so setting one here
 * too would be dead configuration that reads as load-bearing.
 *
 * Rebuilds on every call — cheap (four small object literals plus one more
 * for `query`) — so the caller is responsible for memoizing on
 * `locations`/`templates` identity (`screens.index.tsx` does, keyed on the two
 * queries' `data`). Without that, a fresh array here on every render — and
 * `useLocations` polls on an interval — would churn `useKoreSearch`'s internal
 * memos, which key off `manifest` by reference.
 */
export function screensSearchManifest(
    locations: readonly NamedOption[] | undefined,
    templates: readonly NamedOption[] | undefined,
): FilterResponse {
    return {
        filters: screenFilterDefs(locations ?? [], templates ?? []),
        query: {
            fields: [
                { value: "Name", label: "Name" },
                { value: "LocationName", label: "Location" },
            ],
        },
        sortBy: {
            fields: [
                { field: "Name", label: "Name" },
                { field: "LocationName", label: "Location" },
                { field: "Offset", label: "Offset" },
            ],
            default: { field: "Name", order: "Asc" },
        },
    };
}

/** Same window as locations: inside the server's 1..=1000. */
export const SCREENS_PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * `ScreenSortField` and `ScreenSearchQueryField` restated as values, for
 * `usePageSearch`'s guards. Typed as the wire enums rather than
 * `readonly string[]`, so dropping a variant from `api/types.ts` fails to
 * compile here instead of shipping a field `screens_search.rs` would reject.
 */
export const SCREEN_SORT_FIELDS: readonly ScreenSortField[] = ["Name", "LocationName", "Offset"];
export const SCREEN_QUERY_FIELDS: readonly ScreenSearchQueryField[] = ["Name", "LocationName"];

/** The server's own default, restated so the first request is explicit. */
export const SCREEN_DEFAULT_ORDER: { field: ScreenSortField; order: "Asc" | "Desc" } = {
    field: "Name",
    order: "Asc",
};

/**
 * The raw shape kore's SearchBar actually produces for an `Enum` facet,
 * before translation: `buildFiltersObject` (kore's `search-rq.ts`) emits
 * `{type: "Eq"|"Neq", values}` for any `Enum` def whose parsed filter
 * carries that operator — regardless of what the def's own `operators` list
 * offers. `screenFilterDefs` declares `Eq` only, but that only constrains
 * the *picker UI*; `filter-url.ts`'s `parseFilters` reads the URL directly
 * and `negatedOperatorForKind` maps every `Enum` facet's `!`-prefixed value
 * to `Neq` unconditionally. So `locationId`/`templateId`/`offsetMinutes` can all
 * receive a `Neq` chip from a hand-edited URL even though nothing in this
 * app's UI can produce one.
 */
export interface EnumChip {
    type: "Eq" | "Neq";
    values: string[];
}

/**
 * The shape `useKoreSearch<ScreenSearchBarFilters>` actually produces from
 * `screenFilterDefs` — distinct from `ScreenFilters` (the wire shape
 * `screens_search.rs` expects): every field here is still the raw `Enum`
 * chip and needs `idFilterFromChip`/`offsetMinutesFilter` before it can be
 * sent. Kept separate from `ScreenFilters` rather than reusing it with an
 * `as` cast, so the compiler catches a call site that forgets the
 * translation instead of silently trusting a lie about the runtime shape.
 */
export interface ScreenSearchBarFilters {
    locationId?: EnumChip;
    templateId?: EnumChip;
    active?: BoolOp;
    offsetMinutes?: EnumChip;
}

/**
 * Translate an `Enum` chip into the `IdOp` `locationId`/`templateId` expect on
 * the wire. `IdOp` has only one variant (`Eq`) — there is no `Neq` for the
 * server to receive — so a chip carrying anything else is dropped rather
 * than forwarded. This is load-bearing, not defensive dead code: see the
 * `EnumChip` doc above for exactly how a `Neq` chip reaches here despite the
 * def declaring `operators: [Eq]` and the picker offering no negation
 * affordance. Forwarding it as `IdOp` would ship `{"type":"Neq",...}` to an
 * endpoint whose `IdOp` deserializes only `Eq`, which is a 400 the user did
 * nothing in this app's UI to deserve.
 */
function idFilterFromChip(chip: EnumChip | undefined): IdOp | undefined {
    if (chip?.type !== "Eq" || chip.values.length === 0) return undefined;
    return { type: "Eq", values: chip.values };
}

/**
 * Translate the offset chip into the `NumberOp` the endpoint understands:
 * `applied` -> not zero, `none` -> exactly zero. Kept beside the declaration
 * it belongs to, per the brief.
 *
 * Only the first selected value is consulted — the picker's `Eq`-only
 * operator list and its two-value enum make "applied AND none" a checkbox
 * state a user can reach (selecting both), not a predicate the server can
 * express, so it is treated as "no filter" rather than guessed at.
 */
function offsetMinutesFilter(chip: EnumChip | undefined): NumberOp | undefined {
    if (chip?.values.length !== 1) return undefined;
    switch (chip.values[0]) {
        case "applied":
            return { type: "Neq", values: [0] };
        case "none":
            return { type: "Eq", values: [0] };
        default:
            return undefined;
    }
}

/**
 * Translate kore's raw filter object into the wire shape `screens_search.rs`
 * accepts. Every field needs it: `locationId`/`templateId`/`offsetMinutes` arrive
 * as raw `Enum` chips, and `active` is the only one that is already a wire op.
 *
 * The locations page has the same step (`locationFiltersForWire`); keeping them the
 * same shape is the point, since the operator hole that shipped was one page
 * having this and the other not.
 */
export function screenFiltersForWire(raw: ScreenSearchBarFilters | undefined): ScreenFilters {
    return {
        locationId: idFilterFromChip(raw?.locationId),
        templateId: idFilterFromChip(raw?.templateId),
        active: raw?.active,
        offsetMinutes: offsetMinutesFilter(raw?.offsetMinutes),
    };
}
